import './KPIStatus.scss';
import * as Dfns from 'date-fns/fp';
import { ReactElement, useCallback, useEffect } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { TODAY } from '@blisspointmedia/bpm-types/dist/RelativeDatePicker';
import { Background, Placement } from '../../Components/designConstants';
import { Page } from '../../Components/Page';
import { Skeleton, TableSkeleton } from '../../Components/Skeleton/Skeleton';
import { BPMTable, TypedHeader } from '../../Components/StickyTable';
import { TableEmptyState } from '../../Components/TableEmptyState';
import { WidgetContainer } from '../../Components/WidgetContainer/WidgetContainer';
import { useSetError } from '../../redux/modals';
import { KpiStatusDisplayRow, StatusType, StatusTypeValue } from './kpi-status-constants';
import { useKpiStatus } from './use-kpi-status';

const KPIStatusMessagePopover = ({ message, alert }: { message: string; alert: ReactElement }) => (
  <OverlayTrigger
    placement={Placement.LEFT}
    trigger='click'
    overlay={
      <Popover>
        <Popover.Body className='bpm-kpi-status-popover-body'>{message}</Popover.Body>
      </Popover>
    }
  >
    {alert}
  </OverlayTrigger>
);

const KPIStatusAlert = ({
  status_type,
  message
}: {
  status_type: StatusTypeValue;
  message: string;
}) => {
  switch (status_type) {
    case StatusType.AVAILABLE:
      return (
        <div className='kpi-status-alert kpi-status-alert-success' data-testid='status-available'>
          <h4>Available</h4>
        </div>
      );
    case StatusType.UNAVAILABLE_FAILED:
      return (
        <KPIStatusMessagePopover
          message={message}
          alert={
            <div
              className='kpi-status-alert kpi-status-alert-danger'
              data-testid='status-unavailable-failed'
            >
              <h4>Unavailable</h4>
              <span>(stopped ingestions)</span>
            </div>
          }
        />
      );
    case StatusType.UNAVAILABLE_ALERT_ONLY:
      return (
        <KPIStatusMessagePopover
          message={message}
          alert={
            <div
              className='kpi-status-alert kpi-status-alert-primary'
              data-testid='status-unavailable-alert-only'
            >
              <h4>Non-essential KPI data is unavailable</h4>
              <span>(continuing ingestions)</span>
            </div>
          }
        />
      );
    default:
      return (
        <KPIStatusMessagePopover
          message={message}
          alert={
            <div
              className='kpi-status-alert kpi-status-alert-warning'
              data-testid='status-unavailable-checking'
            >
              <h4>Unavailable</h4>
              <span>(will continue to check every 30 minutes)</span>
            </div>
          }
        />
      );
  }
};

const HEADERS: TypedHeader<KpiStatusDisplayRow>[] = [
  {
    sortPriority: 1,
    sortAscending: true,
    flex: 2,
    label: 'Company',
    name: 'company',
    renderer: data => data.company
  },
  {
    flex: 2,
    label: 'Run Date (Pacific)',
    name: 'run_date_pacific',
    renderer: data => data.run_date_pacific
  },
  {
    flex: 3,
    label: 'Status',
    name: 'status_type',
    renderer: data => (
      <div className='kpi-status-alert-card' data-testid={`status-card-${data.company}`}>
        <KPIStatusAlert status_type={data.status_type} message={data.message} />
      </div>
    )
  },
  {
    flex: 3,
    label: 'Last Checked',
    name: 'last_modified',
    renderer: data => (
      <div className='kpi-status-last-checked'>
        <div className='last-modified-time'>
          {Dfns.format('yyyy-MM-dd kk:mm:ss', Dfns.parseISO(data.last_modified))}
        </div>
        <div className='attempts'>{`Attempt number: ${data.attempts}`}</div>
      </div>
    )
  }
];

export const KPIStatus = () => {
  const setError = useSetError();
  const { data, isLoading, error } = useKpiStatus();

  useEffect(() => {
    if (error) {
      setError({ message: 'Failed to fetch KPI statuses', reportError: error });
    }
  }, [error, setError]);

  // BPMTable has no loading concept of its own, so this fills its noRowsRenderer: a skeleton
  // while fetching, otherwise the empty-state fallback. Keeping BPMTable always mounted (rather
  // than swapping the whole widget for a spinner) keeps the table shell visible throughout.
  const renderTableLoading = useCallback(() => {
    if (isLoading) {
      return (
        <Skeleton>
          <TableSkeleton rowHeight={45} headerHeight={40} yOffset={8} />
        </Skeleton>
      );
    }
    return <TableEmptyState />;
  }, [isLoading]);

  const sortedDates = [...new Set((data ?? []).map(d => d.run_date_pacific))].sort();
  const hasDates = sortedDates.length > 0;
  const maxDate = hasDates ? sortedDates[sortedDates.length - 1] : TODAY;

  return (
    <Page title='KPI Status' pageType='KPI Status' isLoading={isLoading}>
      <div className='bpm-kpi-status'>
        <WidgetContainer className='bpm-kpi-status-widget' background={Background.DARK} fillHeight>
          <div className='bpm-kpi-status-table'>
            <BPMTable
              // `defaultTokens` is captured once at mount (FilterBar's useFilterBarTokens seeds
              // a useState with it, so later prop changes are ignored). Before `data` loads, the
              // options list to match "Run Date (Pacific)" is `run_date_pacific` from real rows.
              // Since we mount immediately, keying on whether data has arrived forces a fresh
              // mount once it does, so the default token gets applied against real data instead
              // of getting silently dropped at the empty pre-load mount.
              //
              // An "is X" token can only ever resolve to a value that already exists among the
              // loaded rows -- FilterBar's parseRawTokenArray only accepts free-form new values
              // after an "is like"/"is not like" operator (see isValidNewOption), never after a
              // plain "is". So when there are no rows at all, no date -- "today" included -- can
              // ever be a valid match; omit the default token entirely rather than emit one that
              // can never resolve (a dangling "Run Date (Pacific) is" chip + a console error).
              key={data ? 'loaded' : 'loading'}
              headers={HEADERS}
              data={data ?? []}
              noRowsRenderer={renderTableLoading}
              rowHeight={45}
              headerHeight={40}
              defaultTokens={
                hasDates ? { advanced: ['Run Date (Pacific)', 'is', maxDate] } : undefined
              }
              defaultAdvancedFilter
            />
          </div>
        </WidgetContainer>
      </div>
    </Page>
  );
};
